import React, { useState, useEffect, useCallback } from 'react';
import api, { errorText } from '../config';
import { useAuth } from '../context/AuthContext';
import {
  Target, Plus, Upload, Loader2, RefreshCw, Search, Send, Check, X,
  Mail, Sparkles, FileEdit, ChevronDown, ChevronRight, Trash2, AlertCircle, Globe2,
} from 'lucide-react';

const STATUS_STYLES = {
  new: 'bg-slate-100 text-slate-600 border-slate-200',
  scored: 'bg-blue-50 text-blue-700 border-blue-200',
  outreach_drafted: 'bg-violet-50 text-violet-700 border-violet-200',
  contacted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  advanced: 'bg-amber-50 text-amber-800 border-amber-300',
};

const DRAFT_STYLES = {
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  sent: 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n}`;
};

function AddTargetForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: '', sector: '', geography: '', contact_name: '', contact_email: '', revenue_usd: '', ebitda_margin_pct: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const payload = { ...form };
    for (const k of ['revenue_usd', 'ebitda_margin_pct']) {
      if (payload[k] !== '' && payload[k] !== null) payload[k] = Number(payload[k]);
      else delete payload[k];
    }
    api.post('/api/sourcing/targets', payload)
      .then(() => { setForm({ company_name: '', sector: '', geography: '', contact_name: '', contact_email: '', revenue_usd: '', ebitda_margin_pct: '', notes: '' }); setOpen(false); onCreated(); })
      .catch((err) => setError(errorText(err, 'Failed to add target.')))
      .finally(() => setBusy(false));
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-700">
        <Plus className="w-4 h-4" /> Add Target
      </button>
    );
  }
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 w-full">
      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <input required value={form.company_name} onChange={set('company_name')} placeholder="Company name *" className="col-span-2 border rounded-lg px-3 py-2 text-sm" />
        <input value={form.sector} onChange={set('sector')} placeholder="Sector" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.geography} onChange={set('geography')} placeholder="Geography" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.contact_name} onChange={set('contact_name')} placeholder="Contact name" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.contact_email} onChange={set('contact_email')} type="email" placeholder="Contact email" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.revenue_usd} onChange={set('revenue_usd')} placeholder="Revenue (USD)" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.ebitda_margin_pct} onChange={set('ebitda_margin_pct')} placeholder="EBITDA margin %" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.notes} onChange={set('notes')} placeholder="Notes" className="col-span-2 md:col-span-4 border rounded-lg px-3 py-2 text-sm" />
        <div className="col-span-2 md:col-span-4 flex items-center gap-2">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save Target
          </button>
          <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          {error && <span className="text-sm text-rose-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{error}</span>}
        </div>
      </form>
    </div>
  );
}

function UniverseCard({ onBuilt }) {
  const [form, setForm] = useState({ business_type: '', location: '', osm_tag: '' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [thesisSectors, setThesisSectors] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get('/api/integrations/thesis')
      .then((r) => setThesisSectors((r.data.sectors || []).filter(Boolean)))
      .catch(() => {});
  }, []);

  const build = () => {
    setBusy(true); setError(''); setResult(null);
    const payload = { business_type: form.business_type, location: form.location, limit: 100 };
    if (form.osm_tag) payload.osm_tag = form.osm_tag;
    api.post('/api/sourcing/targets/universe', payload)
      .then((r) => { setResult(r.data); onBuilt(); })
      .catch((err) => setError(errorText(err, 'Could not build the target list.')))
      .finally(() => setBusy(false));
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-1">
        <Globe2 className="w-4 h-4" /> Build a target list from open data
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Free alternative to paid deal databases. Describe the business type and where to look — Caply pulls
        named companies from OpenStreetMap into your targets. Financials aren't in this data, so revenue and
        margin stay blank until you enrich them.
      </p>
      {thesisSectors.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <span className="text-xs text-slate-500">Your thesis sectors:</span>
          {thesisSectors.map((sec) => (
            <button key={sec} type="button" onClick={() => setForm((f) => ({ ...f, business_type: sec }))}
              className="px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-100">
              {sec}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-2">
        <input value={form.business_type} onChange={set('business_type')} placeholder="Business type — e.g. dental clinics, auto repair, logistics" className="border rounded-lg px-3 py-2 text-sm" />
        <input value={form.location} onChange={set('location')} placeholder="Where — e.g. Austin, Texas" className="border rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="flex items-center justify-between mt-2">
        <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-xs text-slate-500 hover:text-slate-700 underline">
          {showAdvanced ? 'Hide' : 'Advanced'} — custom OSM tag
        </button>
        <button onClick={build} disabled={busy || !form.location || (!form.business_type && !form.osm_tag)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-700 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe2 className="w-4 h-4" />} Build target list
        </button>
      </div>
      {showAdvanced && (
        <input value={form.osm_tag} onChange={set('osm_tag')} placeholder="Raw OSM tag — e.g. shop=bakery (overrides business type)" className="mt-2 w-full border rounded-lg px-3 py-2 text-sm" />
      )}
      {result && (
        <div className="mt-3 text-sm space-y-1">
          <div>
            <span className="text-emerald-700 font-medium">{result.imported} added</span>
            <span className="text-slate-500"> from {result.found} found ({result.sector} in {result.location})</span>
            {result.duplicates > 0 && <span className="text-amber-600"> · {result.duplicates} already in tracker</span>}
            {result.skipped_large > 0 && <span className="text-slate-500"> · {result.skipped_large} large enterprise{result.skipped_large === 1 ? '' : 's'} filtered out</span>}
          </div>
          {result.thesis_alignment && result.thesis_alignment.matches === false && (
            <div className="flex items-start gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                ‘{result.sector}’ doesn’t overlap your investment thesis sectors ({result.thesis_alignment.thesis_sectors.join(', ')}).
                These targets will score low — build with a thesis sector above, or continue if this search is intentional.
              </span>
            </div>
          )}
          {(result.thesis_suggested_types || []).length > 0 && (
            <div className="text-xs text-slate-500">
              For your thesis, try: {result.thesis_suggested_types.map((t, i) => (
                <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, business_type: t }))} className="underline hover:text-slate-800 mr-1.5">{t}{i < result.thesis_suggested_types.length - 1 ? ',' : ''}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {error && <div className="mt-2 text-sm text-rose-600">{error}</div>}
    </div>
  );
}

function ImportCsvCard({ onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true); setError(''); setResult(null);
    api.post('/api/sourcing/targets/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => { setResult(r.data); onImported(); })
      .catch((err) => setError(errorText(err, 'Import failed.')))
      .finally(() => setBusy(false));
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2"><Upload className="w-4 h-4" /> Import targets from CSV</div>
      <p className="text-xs text-slate-500 mb-3">Columns: company_name (required), sector, geography, contact_name, contact_email, contact_title, revenue_usd, ebitda_margin_pct, notes</p>
      <div className="flex items-center gap-2">
        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <button onClick={submit} disabled={!file || busy} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-700 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
        </button>
      </div>
      {result && (
        <div className="mt-3 text-sm">
          <span className="text-emerald-700 font-medium">{result.imported} imported</span>
          {result.duplicates > 0 && <span className="text-amber-600"> · {result.duplicates} duplicates skipped</span>}
          {(result.skipped || []).slice(0, 5).map((s, i) => (
            <div key={i} className="text-xs text-slate-500">Skipped {s.row}: {s.reason}</div>
          ))}
        </div>
      )}
      {error && <div className="mt-2 text-sm text-rose-600">{error}</div>}
    </div>
  );
}

function OutreachCard({ draft, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.body || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canEdit = isAdmin && draft.status === 'pending_approval';

  const review = (action) => {
    setBusy(true); setError('');
    api.put(`/api/sourcing/outreach/${draft.id}/review`, { action, subject: subject !== draft.subject ? subject : undefined, body: body !== draft.body ? body : undefined })
      .then(() => onChanged())
      .catch((err) => setError(errorText(err, 'Review failed.')))
      .finally(() => setBusy(false));
  };

  const send = () => {
    setBusy(true); setError('');
    api.post(`/api/sourcing/outreach/${draft.id}/send`)
      .then(() => onChanged())
      .catch((err) => setError(typeof err?.response?.data?.detail === 'object' ? (err.response.data.detail.reason || 'Send failed.') : (errorText(err, 'Send failed.'))))
      .finally(() => setBusy(false));
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-sm font-medium text-slate-800 truncate">{draft.subject}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">To {draft.to_email} · {draft.company_name} · created {new Date(draft.created_at).toLocaleDateString()}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-xs border ${DRAFT_STYLES[draft.status] || DRAFT_STYLES.pending_approval}`}>{(draft.status || '').replace(/_/g, ' ')}</span>
          <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-700">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          {canEdit ? (
            <>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-800 placeholder-slate-400" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono bg-white text-slate-800 placeholder-slate-400" />
            </>
          ) : (
            <div className="text-sm text-slate-800 whitespace-pre-wrap border border-slate-200 rounded-lg p-3 bg-slate-50">{draft.body}</div>
          )}
          {draft.status === 'pending_approval' && isAdmin && (
            <div className="flex items-center gap-2">
              <button onClick={() => review('approve')} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-500 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
              </button>
              <button onClick={() => review('reject')} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-sm hover:bg-rose-100 disabled:opacity-50">
                <X className="w-4 h-4" /> Reject
              </button>
            </div>
          )}
          {draft.status === 'approved' && isAdmin && (
            <button onClick={send} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-700 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send via Gmail
            </button>
          )}
          {error && <div className="text-sm text-rose-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{error}</div>}
          {draft.status === 'pending_approval' && !isAdmin && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Awaiting admin approval before it can be sent.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Sourcing() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Caply_Admin';
  const [targets, setTargets] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(() => {
    Promise.all([
      api.get('/api/sourcing/targets'),
      api.get('/api/sourcing/outreach'),
      api.get('/api/sourcing/summary'),
    ])
      .then(([t, d, s]) => { setTargets(t.data.targets || []); setDrafts(d.data.drafts || []); setSummary(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const scoreTarget = (t) => {
    setBusyId(t.id);
    api.post(`/api/sourcing/targets/${t.id}/score`)
      .then((r) => showToast(`Scored ${t.company_name}: ${r.data.match_score ?? 'no thesis on file'}`))
      .catch((err) => showToast(err?.response?.data?.detail || 'Scoring failed.', 'err'))
      .finally(() => { setBusyId(null); load(); });
  };

  const scoreAll = () => {
    setBusyId('all');
    api.post('/api/sourcing/targets/score-all')
      .then((r) => showToast(`Scored ${r.data.scored} target${r.data.scored === 1 ? '' : 's'}.`))
      .catch(() => showToast('Scoring failed.', 'err'))
      .finally(() => { setBusyId(null); load(); });
  };

  const advanceDone = (r, label) => {
    const bits = [];
    if (r.data.advanced_count > 0) bits.push(`${r.data.advanced_count} sent to Active Deals`);
    if (r.data.duplicates?.length) bits.push(`${r.data.duplicates.length} duplicate name${r.data.duplicates.length === 1 ? '' : 's'} skipped`);
    if (r.data.already_advanced?.length) bits.push(`${r.data.already_advanced.length} already in pipeline`);
    showToast(bits.length ? `${label}: ${bits.join(' · ')}.` : `${label}: nothing to send.`, r.data.advanced_count > 0 ? 'ok' : 'err');
    load();
  };

  const advanceTarget = (t) => {
    if (!window.confirm(`Send ${t.company_name} to the Active Deals pipeline? Caply will create a deal from this target's details.`)) return;
    setBusyId(t.id);
    api.post('/api/sourcing/targets/advance', { target_ids: [t.id] })
      .then((r) => advanceDone(r, t.company_name))
      .catch((err) => showToast(err?.response?.data?.detail || 'Sending failed.', 'err'))
      .finally(() => setBusyId(null));
  };

  const advanceSelected = () => {
    if (!window.confirm(`Send ${selected.length} selected target${selected.length === 1 ? '' : 's'} to the Active Deals pipeline?`)) return;
    setBulkBusy(true);
    api.post('/api/sourcing/targets/advance', { target_ids: selected })
      .then((r) => { advanceDone(r, 'Sent'); setSelected([]); })
      .catch((err) => showToast(err?.response?.data?.detail || 'Sending failed.', 'err'))
      .finally(() => setBulkBusy(false));
  };

  const draftOutreach = (t) => {
    setBusyId(t.id);
    api.post(`/api/sourcing/outreach/${t.id}/draft`)
      .then(() => showToast(`Draft created for ${t.company_name} — pending admin approval.`))
      .catch((err) => showToast(err?.response?.data?.detail || 'Draft failed.', 'err'))
      .finally(() => { setBusyId(null); load(); });
  };

  const deleteTarget = (t) => {
    if (!window.confirm(`Remove ${t.company_name} from the tracker?`)) return;
    setBusyId(t.id);
    api.delete(`/api/sourcing/targets/${t.id}`)
      .then(() => showToast(`Removed ${t.company_name}.`))
      .catch((err) => showToast(err?.response?.data?.detail || 'Delete failed.', 'err'))
      .finally(() => { setBusyId(null); load(); });
  };

  const shown = targets.filter((t) =>
    !filter || (t.company_name || '').toLowerCase().includes(filter.toLowerCase())
  );

  const toggleSelect = (id) => setSelected((sel) => sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);

  const allShownSelected = shown.length > 0 && shown.every((t) => selected.includes(t.id));
  const toggleSelectAll = () => setSelected(allShownSelected ? [] : shown.map((t) => t.id));

  const bulkDelete = () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Remove ${selected.length} target${selected.length === 1 ? '' : 's'} from the tracker? This can’t be undone.`)) return;
    setBulkBusy(true);
    api.post('/api/sourcing/targets/bulk-delete', { ids: selected })
      .then((r) => {
        const { deleted_count: n, not_found: nf } = r.data;
        showToast(`Removed ${n} target${n === 1 ? '' : 's'}${(nf || []).length ? ` (${nf.length} not found)` : ''}.`);
        setSelected([]);
      })
      .catch((err) => showToast(err?.response?.data?.detail || 'Bulk delete failed.', 'err'))
      .finally(() => { setBulkBusy(false); load(); });
  };

  const draftsFor = (targetId) => drafts.filter((d) => d.target_id === targetId);
  const pendingCount = (summary?.outreach_by_status?.pending_approval) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading sourcing pipeline…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Target className="w-6 h-6" /> Find Deals</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tell Caply what you’re looking for — it finds matching companies and contacts, and drafts outreach that only sends after you approve it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AddTargetForm onCreated={() => { showToast('Target added.'); load(); }} />
          {isAdmin && selected.length > 0 && (
            <>
              <button onClick={advanceSelected} disabled={bulkBusy} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-navy-900 rounded-lg text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send {selected.length} to Active Deals
              </button>
              <button onClick={bulkDelete} disabled={bulkBusy} className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete {selected.length} selected
              </button>
            </>
          )}
          <button onClick={scoreAll} disabled={busyId === 'all' || targets.length === 0} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-40">
            {busyId === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Score all
          </button>
          <button onClick={load} className="p-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Targets', value: summary.total_targets },
            { label: 'Scored', value: summary.targets_by_status?.scored || 0 },
            { label: 'Drafted', value: summary.targets_by_status?.outreach_drafted || 0 },
            { label: 'Contacted', value: summary.targets_by_status?.contacted || 0 },
            { label: 'Pending approval', value: pendingCount },
          ].map((tile) => (
            <div key={tile.label} className="border border-slate-200 rounded-xl p-4 bg-white">
              <div className="text-xs text-slate-500">{tile.label}</div>
              <div className="text-2xl font-semibold text-slate-900 mt-1">{tile.value}</div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`rounded-lg px-4 py-2 text-sm border ${toast.kind === 'err' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>{toast.msg}</div>
      )}

      <UniverseCard onBuilt={() => { showToast('Target list built from open data.'); load(); }} />

      <ImportCsvCard onImported={() => { showToast('Import complete.'); load(); }} />

      {/* Targets table */}
      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Search className="w-4 h-4 text-slate-400" /> Sourcing targets</div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by company…" className="border rounded-lg px-3 py-1.5 text-sm w-56" />
        </div>
        {shown.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No targets yet. Add one above or import a CSV from your banker lists.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50">
                {isAdmin && (
                  <th className="px-2 py-2 w-8">
                    <input type="checkbox" checked={allShownSelected} onChange={toggleSelectAll} aria-label="Select all shown" />
                  </th>
                )}
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 font-medium">Sector / Geo</th>
                <th className="px-4 py-2 font-medium">Revenue</th>
                <th className="px-4 py-2 font-medium">Margin</th>
                <th className="px-4 py-2 font-medium">Score</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <React.Fragment key={t.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50 ${selected.includes(t.id) ? 'bg-rose-50/40' : ''}`}>
                    {isAdmin && (
                      <td className="px-2 py-3">
                        <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} aria-label={`Select ${t.company_name}`} />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button onClick={() => setExpanded(expanded === t.id ? null : t.id)} className="font-medium text-slate-900 hover:underline text-left">
                        {t.company_name}
                      </button>
                      <div className="text-xs text-slate-500">{t.contact_email || 'no contact on file'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.sector || '—'} / {t.geography || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtMoney(t.revenue_usd)}</td>
                    <td className="px-4 py-3 text-slate-600">{t.ebitda_margin_pct != null ? `${t.ebitda_margin_pct}%` : '—'}</td>
                    <td className="px-4 py-3">
                      {t.match_score != null ? (
                        <span className={`font-semibold ${t.match_score >= 70 ? 'text-emerald-700' : t.match_score >= 40 ? 'text-amber-600' : 'text-slate-500'}`}>{t.match_score}</span>
                      ) : <span className="text-slate-400">unscored</span>}
                    </td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[t.status] || STATUS_STYLES.new}`}>{(t.status || 'new').replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => scoreTarget(t)} disabled={busyId === t.id} title="Score against thesis"
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 disabled:opacity-40">
                          {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        </button>
                        <button onClick={() => draftOutreach(t)} disabled={busyId === t.id} title="Draft outreach email"
                          className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-600 disabled:opacity-40">
                          {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileEdit className="w-4 h-4" />}
                        </button>
                        {t.status === 'advanced' ? (
                          <a href="/reports" title={`Already in the pipeline (deal created ${t.advanced_at ? new Date(t.advanced_at).toLocaleDateString() : ''})`}
                            className="p-1.5 inline-flex rounded-lg text-emerald-700 hover:bg-emerald-50">
                            <Check className="w-4 h-4" />
                          </a>
                        ) : (
                          <button onClick={() => advanceTarget(t)} disabled={busyId === t.id} title="Send to the Active Deals pipeline"
                            className="p-1.5 rounded-lg hover:bg-brand-100 text-brand-700 disabled:opacity-40">
                            {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => deleteTarget(t)} disabled={busyId === t.id} title="Delete target"
                            className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600 disabled:opacity-40">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === t.id && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan={isAdmin ? 8 : 7} className="px-4 py-4">
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-medium text-slate-700 mb-1">Thesis match</div>
                            <div className="text-slate-600">{t.match_rationale || 'Not scored yet.'}</div>
                            {t.match_gaps?.length > 0 && (
                              <ul className="mt-2 text-xs text-amber-700 list-disc list-inside">
                                {t.match_gaps.map((g, i) => <li key={i}>{g}</li>)}
                              </ul>
                            )}
                            {t.notes && <div className="mt-2 text-xs text-slate-500">Notes: {t.notes}</div>}
                          </div>
                          <div>
                            <div className="font-medium text-slate-700 mb-1">Outreach drafts</div>
                            {draftsFor(t.id).length === 0 ? (
                              <div className="text-slate-500">No drafts yet.</div>
                            ) : (
                              <div className="space-y-2">
                                {draftsFor(t.id).map((d) => (
                                  <OutreachCard key={d.id} draft={d} isAdmin={isAdmin} onChanged={() => { showToast('Draft updated.'); load(); }} />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pending approvals strip for admins */}
      {isAdmin && pendingCount > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
          <div className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" /> {pendingCount} outreach draft{pendingCount === 1 ? '' : 's'} awaiting your approval
          </div>
          <div className="space-y-2">
            {drafts.filter((d) => d.status === 'pending_approval').map((d) => (
              <OutreachCard key={d.id} draft={d} isAdmin={isAdmin} onChanged={() => load()} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
